import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { startImpersonation } from "@/features/impersonation/impersonation-session";
import { trpcClient } from "@/trpc";

export type ImpersonationTarget = {
  id: string;
  email: string | null;
  first_name: string;
  last_name: string;
};

/** Renders inside a `Dialog`; confirming ends with a full page load as the user. */
export function ImpersonateUserDialog({
  user,
  onCancel,
}: {
  user: ImpersonationTarget;
  onCancel: () => void;
}) {
  const startMutation = useMutation({
    mutationFn: () =>
      trpcClient.impersonation.start.mutate({ userId: user.id }),
    onSuccess: (started) => {
      const fullName = [started.firstName, started.lastName]
        .filter(Boolean)
        .join(" ");
      startImpersonation({
        session: {
          userId: started.userId,
          displayName: fullName || started.email || started.userId,
        },
        experience: started.experience,
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  return (
    <DialogContent className="!w-[calc(100vw-2rem)] !max-w-[480px] !max-h-[calc(100dvh-2rem)] gap-5 overflow-y-auto rounded-2xl border-slate-200 bg-white p-5 text-slate-900 sm:rounded-2xl sm:p-6">
      <DialogHeader className="pr-6 text-left">
        <DialogTitle className="text-xl leading-7">
          Impersonate this user?
        </DialogTitle>
        <DialogDescription className="text-sm leading-6 text-slate-600 [overflow-wrap:anywhere]">
          <span className="block font-medium text-slate-900">
            {user.first_name} {user.last_name}
          </span>
          <span className="block">{user.email}</span>
        </DialogDescription>
      </DialogHeader>
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm leading-6 text-amber-950">
        You will use Saile as this person, with full access. Anything you do,
        such as sending messages or editing credentials, is done as them. It is
        recorded under your own staff account.
      </div>
      <DialogFooter className="mt-0 border-t border-slate-200 pt-4 sm:flex-row sm:space-x-0">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={startMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={() => startMutation.mutate()}
          disabled={startMutation.isPending}
        >
          {startMutation.isPending ? "Starting..." : "Impersonate"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
